/* ==========================================================================
   Search — the predictive overlay, and the search page's supplemental groups.

   One module, because both do the same thing: ask Shopify's locale-aware
   predictive endpoint for a SERVER-RENDERED SECTION and swap it in. Liquid
   renders every row; this fetches, coordinates and swaps. Nothing here builds a
   result.

   Progressive enhancement. Without it the header search control is still a link
   to /search, the search page still filters, sorts and paginates natively, and
   the only thing missing is the overlay and the optional secondary groups —
   which is exactly the deal: the primary product search never depends on this.

   ---- request discipline ---------------------------------------------------

   Results begin at two meaningful characters and wait ~300ms after typing
   stops, so a burst of keystrokes is one request rather than one per key.

   Staleness is guarded twice: an AbortController cancels the request in flight,
   and every request carries a sequence number that is checked again after the
   response lands. An older answer can never replace a newer query, even if the
   abort loses the race.
   ========================================================================== */

/* The overlay covers the page, so it owes the reader the same scroll lock and
   the same focus containment the Panel Shell gives — the SAME ones, not a
   second pair. `overlay.js` is where they live. */
import { scrollLock, trapTab, inertBackground, releaseBackground, closeOtherOverlays } from 'emore-overlay';

const DEBOUNCE = 300;
const MIN_CHARS = 2;

/* Only what V1 renders. Articles and journal are never requested. */
const RESOURCES = 'product,collection,page';

/* The predictive route is locale-aware and comes from Liquid on the element
   that needs it, so nothing here hard-codes /search/suggest. */
const predictiveUrl = (base, params) => {
  const url = new URL(base, window.location.origin);
  Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
  return url.toString();
};

/* ---- the overlay --------------------------------------------------------- */

class EmorePredictive extends HTMLElement {
  connectedCallback() {
    this.surface = this.querySelector('[data-predictive-surface]');
    this.input = this.querySelector('[data-predictive-input]');
    this.region = this.querySelector('[data-predictive-region]');
    this.idle = this.querySelector('[data-predictive-idle]');
    this.errorBox = this.querySelector('[data-predictive-error]');
    this.clearBtn = this.querySelector('[data-predictive-clear]');
    this.scrim = this.querySelector('[data-predictive-scrim]');
    if (!this.surface || !this.input) return;

    this.trigger = null;
    this.sequence = 0;
    this.controller = null;
    this.lastQuery = '';

    this.onKeydown = this.onKeydown.bind(this);

    this.input.addEventListener('input', () => this.onInput());
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-predictive-close]')) this.close();
      if (event.target.closest('[data-predictive-clear]')) this.clear();
      if (event.target.closest('[data-predictive-retry]')) this.run(this.lastQuery, true);
    });
    if (this.scrim) this.scrim.addEventListener('click', () => this.close());
  }

  get isOpen() { return !this.hidden; }

  open(trigger) {
    if (this.isOpen) return;

    /* One overlay owns the screen — enforced in `overlay.js` so the rule runs
       in both directions, not just this one. */
    closeOtherOverlays(this);

    this.trigger = trigger || document.activeElement;

    /* The desktop panel hangs from the bottom of whatever chrome is above it.
       Measured once on open rather than hard-coded, so it stays correct when
       the announcement bar is switched off or the header changes height.

       Shopify stamps the group class on EVERY section in the group, not on a
       wrapper, so the bottom of the chrome is the furthest bottom of them all —
       reading the first one gives the announcement bar and hangs the panel over
       the header. This overlay is itself a section in that group; it is skipped,
       and so are its zero-height neighbours, which cost nothing to include. */
    const sections = document.querySelectorAll('.shopify-section-group-header-group');
    let bottom = 0;
    sections.forEach((section) => {
      if (section.contains(this)) return;
      bottom = Math.max(bottom, section.getBoundingClientRect().bottom);
    });
    if (!bottom) {
      const header = document.querySelector('.header');
      if (header) bottom = Math.max(0, header.getBoundingClientRect().bottom);
    }
    this.style.setProperty('--psearch-top', `${Math.round(bottom)}px`);

    this.hidden = false;
    scrollLock.on();
    this.inerted = inertBackground(this.surface);
    document.addEventListener('keydown', this.onKeydown);

    requestAnimationFrame(() => this.input.focus());
  }

  close() {
    if (!this.isOpen) return;
    this.abort();
    this.hidden = true;
    scrollLock.off();
    releaseBackground(this.inerted);
    this.inerted = null;
    document.removeEventListener('keydown', this.onKeydown);

    const back = this.trigger;
    this.trigger = null;
    if (back && document.contains(back)) back.focus({ preventScroll: true });
  }

  onKeydown(event) {
    if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
    /* The surface declares aria-modal="true", so Tab stays inside it. */
    trapTab(event, this.surface);
  }

  clear() {
    this.input.value = '';
    this.onInput();
    this.input.focus();
  }

  abort() {
    if (this.controller) this.controller.abort();
    this.controller = null;
  }

  onInput() {
    const query = this.input.value.trim();
    if (this.clearBtn) this.clearBtn.hidden = query.length === 0;

    clearTimeout(this.timer);

    /* Idle: a character or two is not yet a query, so the featured collections
       stay and nothing is requested. */
    if (query.length < MIN_CHARS) {
      this.abort();
      this.region.innerHTML = '';
      this.setExpanded(false);
      this.showIdle(true);
      this.showError(false);
      return;
    }

    this.timer = setTimeout(() => this.run(query), DEBOUNCE);
  }

  async run(query, isRetry = false) {
    if (!query) return;
    this.lastQuery = query;
    this.showError(false);

    this.abort();
    this.controller = new AbortController();
    const ticket = ++this.sequence;

    const url = predictiveUrl(this.dataset.url, {
      q: query,
      'resources[type]': RESOURCES,
      'resources[limit]': 4,
      section_id: 'predictive-results'
    });

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error('predictive-failed');
      const html = await response.text();

      /* The second guard. An aborted request usually never gets here, but if a
         slower earlier response does land, its ticket is stale and it is
         dropped rather than overwriting a newer query's results. */
      if (ticket !== this.sequence) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-predictive-results]');
      if (!next) return;

      this.region.innerHTML = next.outerHTML;
      this.setExpanded(true);
      /* Nothing found is not a dead end: the featured collections stay on as
         the way onward. */
      this.showIdle(next.dataset.hasResults !== 'true');
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (ticket !== this.sequence) return;
      /* The interface is never blanked and never navigates itself. */
      this.region.innerHTML = '';
      this.setExpanded(false);
      this.showIdle(true);
      this.showError(true);
    }
  }

  setExpanded(state) {
    this.input.setAttribute('aria-expanded', state ? 'true' : 'false');
  }

  showIdle(state) {
    if (this.idle) this.idle.hidden = !state;
  }

  showError(state) {
    if (this.errorBox) this.errorBox.hidden = !state;
  }
}

if (!customElements.get('emore-predictive')) {
  customElements.define('emore-predictive', EmorePredictive);
}

/* ---- triggers ------------------------------------------------------------
   The header and drawer controls stay real links to /search. A plain click
   opens the overlay instead; a modified click is the browser's.               */

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-search-trigger]');
  if (!trigger) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

  const overlay = document.querySelector('[data-predictive]');
  if (!overlay || typeof overlay.open !== 'function') return;

  event.preventDefault();
  overlay.open(trigger);
});

/* ---- Search again --------------------------------------------------------
   The zero state's leading action. Search Result v1.0 §12: it clears the
   current query and returns focus to the field, and must NOT navigate to
   another search surface. So with JavaScript it does exactly that in place;
   its href is the bare search route, which is this same page with the field
   empty — the honest fallback rather than a button that does nothing.        */

document.addEventListener('click', (event) => {
  const again = event.target.closest('[data-search-again]');
  if (!again) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

  const field = document.querySelector('[data-search-field]');
  if (!field) return;

  event.preventDefault();
  field.value = '';
  field.focus();
});

/* ---- the search page's supplemental groups -------------------------------
   One request, after the product page has already rendered. Collections and
   pages only. A failure leaves the products, filters, sort and pagination
   exactly as they are and says nothing: optional content that did not arrive is
   not an error worth announcing.                                             */

class EmoreSearchSecondary extends HTMLElement {
  async connectedCallback() {
    const query = this.dataset.query;
    const base = this.dataset.url;
    if (!query || !base) return;

    const url = predictiveUrl(base, {
      q: query,
      'resources[type]': 'collection,page',
      'resources[limit]': 3,
      section_id: 'search-secondary-results'
    });

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = doc.querySelector('[data-search-secondary-content]');
      if (next) this.innerHTML = next.outerHTML;
    } catch {
      /* nothing: the groups simply stay absent */
    }
  }
}

if (!customElements.get('emore-search-secondary')) {
  customElements.define('emore-search-secondary', EmoreSearchSecondary);
}

export { EmorePredictive, EmoreSearchSecondary };
